import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { useUsers } from "@/contexts/users-provider";
import { useTranslation } from "@/hooks/useTranslation";
import { TimeSavedDTO } from "@shaarp/common";
import { useEffect, useMemo } from "react";
import { Label, Pie, PieChart } from "recharts";
import { TeamChartLegendItem } from "./team-chart-legend-item";

type TeamTimeSavedChartProps = {
  data: TimeSavedDTO[];
  userColors: Map<string, string>;
  isLoading: boolean;
};

export function TeamTimeSavedChart({
  data,
  userColors,
  isLoading,
}: TeamTimeSavedChartProps) {
  const { t } = useTranslation();
  const { users, apiFetchUsers } = useUsers();

  useEffect(() => {
    apiFetchUsers();
  }, []);

  const chartData = useMemo(() => {
    return data.map((item) => {
      const user = users.find((u) => u.id === item.userId);
      const color = userColors.get(item.userId) ?? "hsl(250, 97%, 62%)";
      return {
        name: user?.fullName || item.userId,
        value: item.timeSaved,
        fill: color,
      };
    });
  }, [data, users, userColors]);

  const chartConfig = useMemo(() => {
    const config: ChartConfig = {};
    data.forEach((item) => {
      const user = users.find((u) => u.id === item.userId);
      const name = user?.fullName || item.userId;
      config[name] = {
        label: name,
        color: userColors.get(item.userId) ?? "hsl(250, 97%, 62%)",
      };
    });
    return config;
  }, [data, users, userColors]);

  const totalTimeSaved = useMemo(() => {
    return data.reduce((acc, item) => acc + item.timeSaved, 0);
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[250px]">
        <div className="animate-pulse text-muted-foreground">
          {t("common.loading")}
        </div>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-[250px]">
        <p className="text-muted-foreground">{t("team.timeSaved.noData")}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <ChartContainer
        config={chartConfig}
        className="mx-auto aspect-square h-[250px] w-[250px]"
      >
        <PieChart>
          <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            innerRadius={60}
            strokeWidth={5}
          >
            <Label
              content={(props: { viewBox?: unknown }) => {
                const viewBox = props.viewBox as
                  | { cx?: number; cy?: number }
                  | undefined;
                if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                  return (
                    <text
                      x={viewBox.cx}
                      y={viewBox.cy}
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      <tspan
                        x={viewBox.cx}
                        y={viewBox.cy}
                        className="fill-foreground text-3xl font-bold"
                      >
                        {totalTimeSaved.toLocaleString()}
                        {t("team.timeSaved.hours", { count: totalTimeSaved })}
                      </tspan>
                      <tspan
                        x={viewBox.cx}
                        y={(viewBox.cy || 0) + 24}
                        className="fill-muted-foreground"
                      >
                        {t("team.timeSaved.saved", { count: totalTimeSaved })}
                      </tspan>
                    </text>
                  );
                }
              }}
            />
          </Pie>
        </PieChart>
      </ChartContainer>
      <ul className="mt-3 flex flex-wrap justify-center gap-x-6 gap-y-1.5 text-xs">
        {chartData.map((item) => (
          <li key={item.name}>
            <TeamChartLegendItem color={item.fill} name={item.name} />
          </li>
        ))}
      </ul>
    </div>
  );
}
